import React, { useState } from "react";
import { useCart } from "@/app/providers/CartProvider";
import { PendingDraftDiscardDialog } from "./PendingDraftDiscardDialog";
import { openPaymentUrl } from "../utils/openPaymentUrl";
import { isPendingDraftExpired } from "../utils/pendingOrderDraft";
import { ordersApi } from "../api/ordersApi";
import { showAppAlert } from "../utils/appDialog";
import "./styles/PendingPaymentBanner.scss";

interface PendingPaymentBannerProps {
  /** Показывать только после проверки API: заказ ещё не оплачен */
  visible: boolean;
}

export const PendingPaymentBanner: React.FC<PendingPaymentBannerProps> = ({
  visible,
}) => {
  const {
    hasPendingOrderDraft,
    pendingOrderDraft,
    restorePendingOrderDraft,
    clearPendingOrderDraft,
    savePendingOrderDraft,
  } = useCart();

  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [paying, setPaying] = useState(false);

  if (
    !visible ||
    !hasPendingOrderDraft ||
    !pendingOrderDraft?.orderId
  ) {
    return null;
  }

  const draft = pendingOrderDraft;
  const expired = isPendingDraftExpired(draft);

  const handlePay = async () => {
    if (expired || paying) return;
    const orderId = draft.orderId;
    if (!orderId) return;
    setPaying(true);
    try {
      // Всегда через API pay — проверка суммы/reuse, не stale URL из черновика.
      const result = await ordersApi.payOrder(orderId);
      savePendingOrderDraft({
        ...draft,
        orderId,
        paymentUrl: result.paymentUrl,
      });
      openPaymentUrl(result.paymentUrl);
    } catch (err) {
      await showAppAlert(
        err instanceof Error ? err.message : "Не удалось открыть оплату"
      );
    } finally {
      setPaying(false);
    }
  };

  const handleRestore = () => {
    restorePendingOrderDraft();
  };

  const handleDiscardClick = () => {
    setDiscardDialogOpen(true);
  };

  const handleDiscardConfirm = () => {
    clearPendingOrderDraft();
  };

  return (
    <>
      <div
        className={`pending-pay-banner ${expired ? "pending-pay-banner--expired" : ""}`}
        role="region"
        aria-label="Незавершённая оплата"
      >
        <div className="pending-pay-banner__text">
          {expired ? (
            <>
              <strong>Срок оплаты истёк.</strong> Верните товары в корзину или
              удалите черновик.
            </>
          ) : (
            <>
              <strong>Заказ не оплачен.</strong>
              {draft.idZakaz || draft.orderId
                ? ` № ${draft.idZakaz || draft.orderId}`
                : ""}{" "}
              Перейдите к оплате или верните товары в корзину.
            </>
          )}
        </div>
        <div className="pending-pay-banner__actions">
          {!expired && (
            <button
              type="button"
              className="pending-pay-banner__btn pending-pay-banner__btn--primary"
              onClick={() => void handlePay()}
              disabled={paying}
            >
              {paying ? "Открываем…" : "Перейти к оплате"}
            </button>
          )}
          <button
            type="button"
            className="pending-pay-banner__btn pending-pay-banner__btn--secondary"
            onClick={handleRestore}
          >
            Вернуть в корзину
          </button>
          <button
            type="button"
            className="pending-pay-banner__btn pending-pay-banner__btn--ghost"
            onClick={handleDiscardClick}
          >
            Удалить черновик
          </button>
        </div>
      </div>
      <PendingDraftDiscardDialog
        open={discardDialogOpen}
        onClose={() => setDiscardDialogOpen(false)}
        onConfirm={handleDiscardConfirm}
        description="Неоплаченный заказ будет сброшен без возврата товаров в корзину. Продолжить?"
      />
    </>
  );
};
